import { useCallback, useMemo } from "react";
import { OrderTypeResponse, SideResponse } from "../../../codegen-api";
import { IPriceSize } from "../../../interfaces/Orderbook";
import { roundToStepSize } from "../../../utils/format";
import { getValueOfContractsAmount } from "../../../utils/orderbook";
import { estimateProjectedLiquidationPrice } from "../../../utils/liquidationPreview";
import { knownPrice } from "../../../utils/unknownNumbers";
import { usePerpsTradeFormValidators } from "./usePerpsTradeFormValidators";

/**
 * What `size` costs against `book`, INCLUDING any part the book cannot fill.
 * `getValueOfContractsAmount` prices only the depth that exists, so 10 BTC
 * against 1 BTC of asks was valued as 1 BTC and passed the buying-power check.
 * The shortfall is priced at the book's worst level, else `fallbackPrice`.
 */
export const bookValueWithShortfall = (
  orderDirection: SideResponse,
  book: IPriceSize[],
  size: number,
  fallbackPrice: number
) => {
  const filledValue = getValueOfContractsAmount(orderDirection, book, size);
  const depth = book.reduce((sum, level) => sum + Number(level[1] || 0), 0);
  const shortfall = size - depth;
  if (shortfall <= 0) return filledValue;
  const prices = book.map((level) => Number(level[0])).filter((p) => p > 0);
  const worstPrice = prices.length
    ? orderDirection === SideResponse.Buy
      ? Math.max(...prices)
      : Math.min(...prices)
    : fallbackPrice;
  return filledValue + shortfall * (worstPrice || 0);
};

/**
 * How much of the asset `usd` buys walking `levels` best-first, and the USD the
 * book cannot absorb. The size is capped at the book's depth (as it always has
 * been); `unfilledUsd > 0` is what drives the insufficient-liquidity warning.
 * No other price is ever substituted for missing depth: a stale limit price
 * left in the form once sized a market order larger than the amount typed.
 */
export const sizeForUsdAgainstBook = (
  // Order book levels as the API and websocket send them: [price, size].
  levels: ReadonlyArray<ReadonlyArray<string | number>> | undefined,
  usd: number
): { size: number; unfilledUsd: number } => {
  let unfilledUsd = usd > 0 ? usd : 0;
  let size = 0;
  for (const [levelPrice, levelSize] of levels || []) {
    if (!unfilledUsd) break;
    const priceNum = Number(levelPrice);
    const sizeNum = Number(levelSize);
    // A level priced 0 costs 0, so the budget is never spent against it and its
    // entire size used to be added for FREE: $100 sized 5.0014 BTC (~$350k)
    // rather than 0.00142857, with the CTA enabled. Zero prices reach here two
    // ways -- `rawToDecimal` returns the string "0" for a falsy value and inside
    // its catch, and the book aggregator collapses every price below 5e-9 onto a
    // single level it rebuilds as "0". NaN is excluded by the same comparison.
    // `bookValueWithShortfall` above has always filtered `p > 0`; this restores
    // the same guard on the path that sizes the order.
    if (!(priceNum > 0 && sizeNum > 0)) continue;
    const levelCost = priceNum * sizeNum;
    if (unfilledUsd > levelCost) {
      unfilledUsd -= levelCost;
      size += sizeNum;
    } else {
      size += unfilledUsd / priceNum;
      unfilledUsd = 0;
    }
  }
  return { size, unfilledUsd };
};

/**
 * The execution price to preview with when the book cannot price the order.
 *
 * A MARKET order must never fall back to `price`: that is the limit field, and
 * it holds whatever was last typed in Limit mode. The rule is stated at the top
 * of this file and honoured by the sizing and notional paths, but the preview
 * ignored it -- and the preview feeds `estimateProjectedLiquidationPrice`, so
 * switching to Limit, typing 50,000, and switching back to Market projected a
 * liquidation price from a number that no longer applied.
 *
 * A LIMIT order legitimately prefers its own price: that IS the price it will
 * execute at if it fills.
 */
export const previewFallbackPrice = (
  orderType: OrderTypeResponse | undefined,
  price: string | number | undefined | null,
  markPrice: string | number | undefined | null
): number =>
  orderType === OrderTypeResponse.Market
    ? Number(markPrice || 0)
    : Number(price || markPrice || 0);

export function usePerpsTradeFormNotional(
  ctx: ReturnType<typeof usePerpsTradeFormValidators>
) {
  const {
    contractPriceStep,
    markPrice,
    orderDirection,
    orderType,
    orderbookData,
    price,
    stopType,
    triggerPrice,
    tradeUseUSDCTerms,
    amount,
    openingFeeRate,
    selectedLeverage,
    accountData,
    currentPosition,
    maxLeverage,
  } = ctx;

  const getRealContractSize = useCallback(
    (amt: string, isUSD?: boolean) => {
      // if asset is USD and market order,
      // go through OB to figure out how many contracts we can buy
      if (isUSD) {
        const usdAmount = Number(amt || 0);
        const stepSize = contractPriceStep.amount_step;
        const precision = contractPriceStep.amount_precision;

        if (orderbookData && orderType === OrderTypeResponse.Market) {
          const bidsAsks =
            orderDirection === SideResponse.Buy
              ? orderbookData.asks
              : orderbookData.bids;
          const { size } = sizeForUsdAgainstBook(bidsAsks, usdAmount);
          return roundToStepSize(size, stepSize, precision);
        }

        // Reached when the book cannot size the order — no depth, or no book
        // at all (its decimals have not arrived, so it cannot be scaled).
        //
        // A MARKET order must never fall back to `price`: that is the limit
        // field, which holds whatever was last typed in Limit mode. Sizing
        // $1,000 at a stale 50,000 sends 0.02 BTC where the mark of 70,000
        // means 0.01428571 — a 40% larger position than the trader asked for.
        const conversionPrice = Number(
          stopType
            ? triggerPrice
            : orderType === OrderTypeResponse.Market
            ? markPrice || 0
            : price || markPrice || 0
        );
        if (usdAmount > 0 && conversionPrice > 0) {
          return roundToStepSize(
            usdAmount / conversionPrice,
            stepSize,
            precision
          );
        }
      }

      // if asset is underlying, just use size
      return Number(amt || 0);
    },
    [
      contractPriceStep.amount_precision,
      contractPriceStep.amount_step,
      markPrice,
      orderDirection,
      orderType,
      orderbookData,
      price,
      stopType,
      triggerPrice,
    ]
  );

  const calculateTotalValueWithSize = useCallback(
    (amountStr: string, isUSDC?: boolean) => {
      let total = 0;

      const isUSD = isUSDC || tradeUseUSDCTerms;

      if (isUSD) {
        return Number(amountStr);
      }

      const realSize = getRealContractSize(amountStr, false);

      if (stopType) {
        // If is a stop order, calculate total using trigger price instead
        total = realSize * (Number(triggerPrice) || 0);
      } else if (orderType === OrderTypeResponse.Limit && price) {
        // If is a limit order, calculate total using the limit price
        total = realSize * Number(price);
      } else {
        // For market orders, use orderbook data
        total = bookValueWithShortfall(
          orderDirection,
          orderDirection === SideResponse.Buy
            ? (orderbookData?.asks as IPriceSize[]) || []
            : (orderbookData?.bids as IPriceSize[]) || [],
          realSize,
          Number(markPrice || 0)
        );
      }

      return total;
    },
    [
      tradeUseUSDCTerms,
      getRealContractSize,
      stopType,
      triggerPrice,
      orderType,
      price,
      markPrice,
      orderDirection,
      orderbookData?.asks,
      orderbookData?.bids,
    ]
  );

  const realContractSize = useMemo(
    () => getRealContractSize(amount, tradeUseUSDCTerms),
    [amount, getRealContractSize, tradeUseUSDCTerms]
  );
  const orderNotional = useMemo(
    () => calculateTotalValueWithSize(amount),
    [amount, calculateTotalValueWithSize]
  );
  const previewOrderNotional = useMemo(() => {
    if (realContractSize <= 0) return 0;

    if (stopType) {
      return realContractSize * Number(triggerPrice || 0);
    }

    if (orderType === OrderTypeResponse.Limit && price) {
      return realContractSize * Number(price);
    }

    const orderbookNotional = bookValueWithShortfall(
      orderDirection,
      orderDirection === SideResponse.Buy
        ? (orderbookData?.asks as IPriceSize[]) || []
        : (orderbookData?.bids as IPriceSize[]) || [],
      realContractSize,
      // A market order: the mark, never a limit price left in the form.
      Number(markPrice || 0)
    );
    if (orderbookNotional > 0) return orderbookNotional;

    // Reached only without a limit price (market, or limit with the price
    // still empty), so the mark is the only honest fallback.
    const fallbackPrice = Number(markPrice || 0);
    return fallbackPrice > 0 ? realContractSize * fallbackPrice : 0;
  }, [
    markPrice,
    orderDirection,
    orderType,
    orderbookData?.asks,
    orderbookData?.bids,
    price,
    realContractSize,
    stopType,
    triggerPrice,
  ]);
  // An unknown fee rate (no verified schedule) gives an unknown fee, not $0:
  // "Est. Fee" and "Required Margin" then show their unknown state.
  const estimatedOpeningFee = !orderNotional
    ? 0
    : openingFeeRate === undefined
    ? undefined
    : orderNotional * openingFeeRate;
  const previewOpeningFee = !previewOrderNotional
    ? 0
    : openingFeeRate === undefined
    ? undefined
    : previewOrderNotional * openingFeeRate;
  const requiredMarginWithFee =
    orderNotional && selectedLeverage && estimatedOpeningFee !== undefined
      ? orderNotional / selectedLeverage + estimatedOpeningFee
      : undefined;
  const previewExecutionPrice = useMemo(() => {
    if (realContractSize > 0 && previewOrderNotional > 0) {
      return previewOrderNotional / realContractSize;
    }
    return previewFallbackPrice(orderType, price, markPrice);
  }, [markPrice, orderType, previewOrderNotional, price, realContractSize]);
  const projectedLiquidation = useMemo(
    () =>
      // The fee reduces the account term: unknown fee, no projection.
      previewOpeningFee === undefined
        ? undefined
        : estimateProjectedLiquidationPrice({
        account: accountData,
        currentPosition,
        orderSide: orderDirection,
        orderSize: realContractSize,
        executionPrice: previewExecutionPrice,
        orderNotional: previewOrderNotional,
        // The mark, or nothing: the execution price is not a mark.
        markPrice: knownPrice(markPrice),
        estimatedFee: previewOpeningFee,
        maxLeverage,
      }),
    [
      accountData,
      currentPosition,
      orderDirection,
      realContractSize,
      previewExecutionPrice,
      previewOrderNotional,
      markPrice,
      previewOpeningFee,
      maxLeverage,
    ]
  );
  const liquidationPriceToShow =
    projectedLiquidation?.liquidationPrice ??
    (currentPosition?.liquidation_price &&
    Number.isFinite(Number(currentPosition.liquidation_price)) &&
    Number(currentPosition.liquidation_price) > 0
      ? Number(currentPosition.liquidation_price)
      : undefined);

  // A market order entered in USDC for more than the book can fill: the size is
  // capped at the book's depth, so the liquidity warning must come from the USD
  // that is left over rather than from comparing the capped size to the book.
  const usdExceedsBookDepth = useMemo(() => {
    if (!tradeUseUSDCTerms || orderType !== OrderTypeResponse.Market) return false;
    const usd = Number(amount || 0);
    if (!(usd > 0) || !orderbookData) return false;
    const levels =
      orderDirection === SideResponse.Buy ? orderbookData.asks : orderbookData.bids;
    return sizeForUsdAgainstBook(levels, usd).unfilledUsd > 0;
  }, [amount, orderDirection, orderType, orderbookData, tradeUseUSDCTerms]);

  return {
    ...ctx,
    usdExceedsBookDepth,
    getRealContractSize,
    calculateTotalValueWithSize,
    realContractSize,
    orderNotional,
    previewOrderNotional,
    estimatedOpeningFee,
    previewOpeningFee,
    requiredMarginWithFee,
    previewExecutionPrice,
    projectedLiquidation,
    liquidationPriceToShow,
  };
}
